<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header con título y acciones -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Reporte de Productos
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ productosOriginales.length }}
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 font-medium mt-0.5">Visualiza y exporta el inventario de productos con múltiples filtros</p>
      </div>
      
      <div class="flex gap-2 flex-wrap w-full sm:w-auto">
        <button
          @click="imprimirReporteCompleto"
          class="flex items-center px-3 sm:px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-xl transition-all duration-300 shadow hover:shadow-md text-[10px] sm:text-xs font-black tracking-widest gap-1.5 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading"
        >
          <Icon icon="mdi:printer" class="text-sm sm:text-base" />
          <span class="hidden xs:inline">Imprimir Reporte</span>
          <span class="xs:hidden">Imprimir</span>
        </button>
        <button
          @click="exportarExcel"
          class="flex items-center px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-300 shadow hover:shadow-md text-[10px] sm:text-xs font-black tracking-widest gap-1.5 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading"
        >
          <Icon icon="mdi:file-excel" class="text-sm sm:text-base" />
          <span class="hidden xs:inline">Exportar Excel</span>
          <span class="xs:hidden">Excel</span>
        </button>
        <button
          @click="exportarPDF"
          class="flex items-center px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-300 shadow hover:shadow-md text-[10px] sm:text-xs font-black tracking-widest gap-1.5 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading"
        >
          <Icon icon="mdi:file-pdf" class="text-sm sm:text-base" />
          <span class="hidden xs:inline">Exportar PDF</span>
          <span class="xs:hidden">PDF</span>
        </button>
      </div>
    </div>

    <!-- Filtros combinados -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-4 sm:mb-6 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <!-- Filtro por estado -->
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1 uppercase">
            Estado del Producto
          </label>
          <select
            v-model="filters.estado"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="">Todos</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>

        <!-- Filtro por categoría -->
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1 uppercase">
            Categoría
          </label>
          <select
            v-model="filters.categoria_id"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="">Todas las categorías</option>
            <option v-for="cat in categorias" :key="cat.idCategoria" :value="cat.idCategoria">
              {{ cat.nombre }}
            </option>
          </select>
        </div>

        <!-- Filtro por nivel de stock -->
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1 uppercase">
            Nivel de Stock
          </label>
          <select
            v-model="filters.nivel_stock"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="">Todos</option>
            <option value="sin_stock">Sin Stock (0)</option>
            <option value="stock_bajo">Stock Bajo (≤ mínimo)</option>
            <option value="stock_normal">Stock Normal (> mínimo)</option>
            <option value="con_stock">Con Stock (> 0)</option>
          </select>
        </div>

        <!-- Filtro por búsqueda -->
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1 uppercase">
            Buscar producto
          </label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input
              type="text"
              v-model="filters.busqueda"
              placeholder="Nombre o código..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
      </div>

      <!-- Filtro por rango de stock -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1 uppercase">
            Stock mínimo
          </label>
          <input
            type="number"
            v-model="filters.stock_min"
            placeholder="Desde"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-bold text-gray-800 dark:text-white outline-none transition-all text-sm sm:text-base"
          />
        </div>
        <div>
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1 uppercase">
            Stock máximo
          </label>
          <input
            type="number"
            v-model="filters.stock_max"
            placeholder="Hasta"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-bold text-gray-800 dark:text-white outline-none transition-all text-sm sm:text-base"
          />
        </div>
      </div>

      <!-- Botones de acción -->
      <div class="flex flex-col sm:flex-row justify-end gap-2 mt-4">
        <button
          @click="applyFilters"
          class="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all shadow-md flex items-center justify-center gap-2"
        >
          <Icon icon="mdi:filter" class="text-sm sm:text-base" />
          Aplicar Filtros
        </button>
        <button
          @click="clearFilters"
          class="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
        >
          <Icon icon="mdi:filter-off" class="text-sm sm:text-base" />
          Limpiar Todo
        </button>
      </div>
    </div>

    <!-- Resumen de estadísticas -->
    <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6">
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Total</p>
            <p class="text-lg sm:text-2xl font-black text-gray-800 dark:text-white">{{ estadisticas.total || 0 }}</p>
          </div>
          <div class="p-2 sm:p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
            <Icon icon="mdi:package-variant" class="text-blue-500 text-lg sm:text-2xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Activos</p>
            <p class="text-lg sm:text-2xl font-black text-green-600 dark:text-green-400">{{ estadisticas.activos || 0 }}</p>
          </div>
          <div class="p-2 sm:p-2.5 bg-green-100 dark:bg-green-900/30 rounded-xl">
            <Icon icon="mdi:check-circle" class="text-green-500 text-lg sm:text-2xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all hidden sm:flex">
        <div class="flex items-center justify-between w-full">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Inactivos</p>
            <p class="text-lg sm:text-2xl font-black text-red-600 dark:text-red-400">{{ estadisticas.inactivos || 0 }}</p>
          </div>
          <div class="p-2 sm:p-2.5 bg-red-100 dark:bg-red-900/30 rounded-xl">
            <Icon icon="mdi:close-circle" class="text-red-500 text-lg sm:text-2xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all hidden md:flex">
        <div class="flex items-center justify-between w-full">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Stock Total</p>
            <p class="text-lg sm:text-2xl font-black text-gray-800 dark:text-white">{{ estadisticas.stockTotal || 0 }}</p>
          </div>
          <div class="p-2 sm:p-2.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
            <Icon icon="mdi:warehouse" class="text-yellow-500 text-lg sm:text-2xl" />
          </div>
        </div>
      </div>
      
      <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">Valor USD</p>
            <p class="text-sm sm:text-2xl font-black text-indigo-600 dark:text-indigo-400">{{ formatCurrencyUSD(estadisticas.valorInventarioUSD) }}</p>
          </div>
          <div class="p-2 sm:p-2.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
            <Icon icon="mdi:cash" class="text-indigo-500 text-lg sm:text-2xl" />
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de stock -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
      <div class="bg-orange-50 dark:bg-orange-900/20 rounded-2xl p-3 sm:p-4 border-l-4 border-orange-500 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-orange-600 dark:text-orange-400 uppercase tracking-widest">Sin Stock</p>
            <p class="text-lg sm:text-2xl font-black text-orange-700 dark:text-orange-300">{{ estadisticas.sinStock || 0 }}</p>
          </div>
          <Icon icon="mdi:package-variant-closed" class="text-orange-500 text-xl sm:text-3xl" />
        </div>
      </div>
      
      <div class="bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl p-3 sm:p-4 border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-yellow-600 dark:text-yellow-400 uppercase tracking-widest">Stock Bajo</p>
            <p class="text-lg sm:text-2xl font-black text-yellow-700 dark:text-yellow-300">{{ estadisticas.stockBajo || 0 }}</p>
          </div>
          <Icon icon="mdi:alert" class="text-yellow-500 text-xl sm:text-3xl" />
        </div>
      </div>
      
      <div class="bg-green-50 dark:bg-green-900/20 rounded-2xl p-3 sm:p-4 border-l-4 border-green-500 shadow-sm hover:shadow-md transition-all hidden sm:flex">
        <div class="flex items-center justify-between w-full">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-green-600 dark:text-green-400 uppercase tracking-widest">Stock Normal</p>
            <p class="text-lg sm:text-2xl font-black text-green-700 dark:text-green-300">{{ estadisticas.stockNormal || 0 }}</p>
          </div>
          <Icon icon="mdi:check-decagram" class="text-green-500 text-xl sm:text-3xl" />
        </div>
      </div>
      
      <div class="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-3 sm:p-4 border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Reposición</p>
            <p class="text-lg sm:text-2xl font-black text-blue-700 dark:text-blue-300">{{ estadisticas.necesitaReposicion || 0 }}</p>
          </div>
          <Icon icon="mdi:truck-delivery" class="text-blue-500 text-xl sm:text-3xl" />
        </div>
      </div>
    </div>

    <!-- Tabla de productos -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <!-- Versión Desktop: Tabla completa -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest">Código</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest">Producto</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest">Categoría</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-center">Stock</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-center">Mín.</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-right">Compra ($)</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-right">Venta ($)</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-right">Valor Total</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-3 text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest text-center">Nivel</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="10" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-else-if="productosFiltrados.length === 0">
              <td colspan="10" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:package-variant-closed" class="text-6xl sm:text-7xl mb-3" />
                  <h4 class="text-base sm:text-xl font-black text-gray-400 tracking-widest">No hay productos</h4>
                  <p class="text-xs sm:text-sm text-gray-400 mt-2">No se encontraron productos con los filtros seleccionados</p>
                </div>
              </td>
            </tr>
            <tr v-for="producto in productosPaginados" :key="producto.idProducto" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-black text-xs border border-gray-200 dark:border-gray-600">
                  {{ producto.codigo || 'N/A' }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div>
                  <p class="font-black text-gray-900 dark:text-white text-sm">{{ producto.nombre }}</p>
                  <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ producto.marca || '' }} {{ producto.modelo || '' }}</p>
                </div>
              </td>
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg font-black text-[10px] border border-purple-100 dark:border-purple-800">
                  {{ producto.nombreCategoria || 'Sin categoría' }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span class="text-base sm:text-lg font-black"
                  :class="{
                    'text-red-600': (producto.stock || 0) === 0,
                    'text-orange-600': (producto.stock || 0) > 0 && (producto.stock || 0) <= (producto.stock_minimo || 0),
                    'text-green-600': (producto.stock || 0) > (producto.stock_minimo || 0)
                  }">
                  {{ producto.stock || 0 }}
                </span>
              </td>
              <td class="px-5 py-4 text-center text-sm text-gray-500">
                {{ producto.stock_minimo || 0 }}
              </td>
              <td class="px-5 py-4 text-right font-bold text-gray-900 dark:text-white text-sm">
                {{ formatCurrencyUSD(producto.precio_compra_usdt) }}
              </td>
              <td class="px-5 py-4 text-right font-bold text-gray-900 dark:text-white text-sm">
                {{ formatCurrencyUSD(producto.precio_venta_usd) }}
              </td>
              <td class="px-5 py-4 text-right font-black text-indigo-600 dark:text-indigo-400 text-sm">
                {{ formatCurrencyUSD((producto.stock || 0) * (producto.precio_compra_usdt || 0)) }}
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="{
                  'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200': producto.estado?.toLowerCase() === 'activo',
                  'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200': producto.estado?.toLowerCase() === 'inactivo'
                }" class="px-2.5 py-1 text-[9px] sm:text-[10px] font-black rounded-full border shadow-sm">
                  {{ producto.estado === 'activo' ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="getStockBadgeClass(producto.stock, producto.stock_minimo)" class="px-2.5 py-1 text-[9px] sm:text-[10px] font-black rounded-full shadow-sm">
                  {{ getStockTexto(producto.stock, producto.stock_minimo) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="productosFiltrados.length > 0" class="bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
            <tr>
              <td colspan="7" class="px-5 py-3 text-right font-black text-gray-700 dark:text-gray-300 text-sm">
                Total Valor Inventario:
              </td>
              <td class="px-5 py-3 text-right font-black text-indigo-600 dark:text-indigo-400 text-sm">
                {{ formatCurrencyUSD(valorInventarioTotal) }}
              </td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2 sm:p-3">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 animate-pulse">
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!loading && productosFiltrados.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:package-variant-closed" class="text-5xl sm:text-6xl mb-3" />
            <h4 class="text-base sm:text-lg font-black text-gray-400 tracking-widest">No hay productos</h4>
          </div>
        </div>
        
        <div v-for="producto in productosPaginados" :key="producto.idProducto" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-3 sm:p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex items-center justify-between mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2 min-w-0">
              <div class="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:package-variant" class="text-purple-600 dark:text-purple-400 text-sm sm:text-base" />
              </div>
              <div class="min-w-0">
                <p class="font-black text-gray-900 dark:text-white text-sm truncate">{{ producto.nombre }}</p>
                <p class="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">{{ producto.codigo || 'N/A' }}</p>
              </div>
            </div>
            <span :class="{
              'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200': producto.estado?.toLowerCase() === 'activo',
              'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200': producto.estado?.toLowerCase() === 'inactivo'
            }" class="px-2 py-0.5 text-[8px] sm:text-[10px] font-black rounded-full border shadow-sm flex-shrink-0">
              {{ producto.estado === 'activo' ? 'Activo' : 'Inactivo' }}
            </span>
          </div>

          <div class="grid grid-cols-2 gap-1 sm:gap-2 mb-2 text-[10px] sm:text-xs">
            <div>
              <span class="text-gray-400 font-black uppercase text-[8px] sm:text-[9px]">Categoría</span>
              <p class="font-bold text-gray-800 dark:text-white truncate">{{ producto.nombreCategoria || 'Sin categoría' }}</p>
            </div>
            <div>
              <span class="text-gray-400 font-black uppercase text-[8px] sm:text-[9px]">Marca/Modelo</span>
              <p class="font-bold text-gray-800 dark:text-white truncate">{{ producto.marca || '' }} {{ producto.modelo || '' }}</p>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-1 sm:gap-2 mb-2 text-[10px] sm:text-xs bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2 sm:p-3">
            <div class="text-center">
              <span class="block text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">Stock</span>
              <span class="text-base sm:text-lg font-black"
                :class="{
                  'text-red-600': (producto.stock || 0) === 0,
                  'text-orange-600': (producto.stock || 0) > 0 && (producto.stock || 0) <= (producto.stock_minimo || 0),
                  'text-green-600': (producto.stock || 0) > (producto.stock_minimo || 0)
                }">
                {{ producto.stock || 0 }}
              </span>
            </div>
            <div class="text-center">
              <span class="block text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">Mín.</span>
              <span class="text-sm sm:text-base font-black text-gray-600">{{ producto.stock_minimo || 0 }}</span>
            </div>
            <div class="text-center">
              <span class="block text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">Compra</span>
              <span class="text-xs sm:text-sm font-bold text-gray-700">{{ formatCurrencyUSD(producto.precio_compra_usdt) }}</span>
            </div>
            <div class="text-center">
              <span class="block text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">Venta</span>
              <span class="text-xs sm:text-sm font-bold text-gray-700">{{ formatCurrencyUSD(producto.precio_venta_usd) }}</span>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div>
              <span class="block text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-widest">Valor Total</span>
              <span class="text-sm sm:text-base font-black text-indigo-600 dark:text-indigo-400">
                {{ formatCurrencyUSD((producto.stock || 0) * (producto.precio_compra_usdt || 0)) }}
              </span>
            </div>
            <span :class="getStockBadgeClass(producto.stock, producto.stock_minimo)" class="px-2.5 py-1 text-[8px] sm:text-[10px] font-black rounded-full shadow-sm">
              {{ getStockTexto(producto.stock, producto.stock_minimo) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="productosFiltrados.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[9px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ (pagination.page - 1) * pagination.perPage + 1 }} - 
          {{ Math.min(pagination.page * pagination.perPage, pagination.total) }} DE {{ pagination.total }}
          <span v-if="productosOriginales.length !== productosFiltrados.length" class="ml-1 sm:ml-2 text-indigo-500">
            (Filtrados de {{ productosOriginales.length }})
          </span>
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button
            @click="prevPage"
            :disabled="pagination.page === 1"
            class="p-1.5 rounded-lg border transition-all disabled:opacity-30 disabled:cursor-not-allowed border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 shadow-sm"
          >
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <span class="px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-black text-gray-600 dark:text-gray-300">
            {{ pagination.page }} / {{ pagination.lastPage }}
          </span>
          <button
            @click="nextPage"
            :disabled="pagination.page >= pagination.lastPage"
            class="p-1.5 rounded-lg border transition-all disabled:opacity-30 disabled:cursor-not-allowed border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 shadow-sm"
          >
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerProductos, obtenerProductosPorCategoria, obtenerEstadisticasProductosLocal, buscarProductosLocal, filtrarProductosPorEstado, ordenarProductos, exportarExcel, exportarPDF } from "@/api/productos.js";
import { obtenerCategorias } from "@/api/categorias.js";
import { imprimirReporteProductos, calcularEstadisticasReporte, cargarLogo } from "@/utils/reporteProductosGenerator";

export default {
  name: 'ReporteProductos',
  components: {
    Icon
  },
  data() {
    return {
      loading: false,
      productosOriginales: [],
      categorias: [],
      filters: {
        estado: '',
        categoria_id: '',
        nivel_stock: '',
        busqueda: '',
        stock_min: '',
        stock_max: ''
      },
      pagination: {
        page: 1,
        perPage: 10,
        total: 0,
        lastPage: 1
      }
    };
  },
  computed: {
    productosFiltrados() {
      let resultado = [...this.productosOriginales];
      
      if (this.filters.estado) {
        resultado = filtrarProductosPorEstado(resultado, this.filters.estado);
      }
      
      if (this.filters.categoria_id) {
        resultado = resultado.filter(p => p.idCategoria === parseInt(this.filters.categoria_id));
      }
      
      if (this.filters.nivel_stock) {
        resultado = resultado.filter(p => {
          const stock = p.stock || 0;
          const stockMinimo = p.stock_minimo || 0;
          
          switch (this.filters.nivel_stock) {
            case 'sin_stock': return stock === 0;
            case 'stock_bajo': return stock > 0 && stockMinimo > 0 && stock <= stockMinimo;
            case 'stock_normal': return stock > 0 && (stockMinimo === 0 || stock > stockMinimo);
            case 'con_stock': return stock > 0;
            default: return true;
          }
        });
      }
      
      if (this.filters.busqueda) {
        resultado = buscarProductosLocal(resultado, this.filters.busqueda);
      }
      
      if (this.filters.stock_min !== '') {
        resultado = resultado.filter(p => (p.stock || 0) >= parseInt(this.filters.stock_min));
      }
      if (this.filters.stock_max !== '') {
        resultado = resultado.filter(p => (p.stock || 0) <= parseInt(this.filters.stock_max));
      }
      
      return resultado;
    },
    
    productosPaginados() {
      const start = (this.pagination.page - 1) * this.pagination.perPage;
      const end = start + this.pagination.perPage;
      return this.productosFiltrados.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.productosFiltrados.length / this.pagination.perPage);
    },
    
    estadisticas() {
      const stats = obtenerEstadisticasProductosLocal(this.productosFiltrados);
      return {
        total: stats.total,
        activos: stats.activos,
        inactivos: stats.inactivos,
        stockTotal: stats.stockTotal || this.productosFiltrados.reduce((sum, p) => sum + (p.stock || 0), 0),
        valorInventarioUSD: stats.valorInventario,
        sinStock: this.productosFiltrados.filter(p => (p.stock || 0) === 0).length,
        stockBajo: this.productosFiltrados.filter(p => {
          const stock = p.stock || 0;
          const min = p.stock_minimo || 0;
          return stock > 0 && min > 0 && stock <= min;
        }).length,
        stockNormal: this.productosFiltrados.filter(p => {
          const stock = p.stock || 0;
          const min = p.stock_minimo || 0;
          return stock > 0 && (min === 0 || stock > min);
        }).length,
        necesitaReposicion: this.productosFiltrados.filter(p => {
          const stock = p.stock || 0;
          const min = p.stock_minimo || 0;
          return stock === 0 || (stock > 0 && min > 0 && stock <= min);
        }).length
      };
    },
    
    valorInventarioTotal() {
      return this.productosFiltrados.reduce((total, p) => 
        total + ((p.stock || 0) * (p.precio_compra_usdt || 0)), 0);
    }
  },
  watch: {
    productosFiltrados: {
      handler() {
        this.pagination.total = this.productosFiltrados.length;
        this.pagination.lastPage = Math.ceil(this.pagination.total / this.pagination.perPage);
        if (this.pagination.page > this.pagination.lastPage && this.pagination.lastPage > 0) {
          this.pagination.page = this.pagination.lastPage;
        }
      },
      immediate: true
    }
  },
  async mounted() {
    await cargarLogo();
    await this.loadCategorias();
    await this.loadProductos();
  },
  methods: {
    formatCurrencyUSD(value) {
      if (!value && value !== 0) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    
    getStockTexto(stock, stockMinimo) {
      const stockNum = stock || 0;
      const stockMinimoNum = stockMinimo || 0;
      
      if (stockNum === 0) return 'SIN STOCK';
      if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) return 'STOCK BAJO';
      return 'NORMAL';
    },
    
    getStockBadgeClass(stock, stockMinimo) {
      const stockNum = stock || 0;
      const stockMinimoNum = stockMinimo || 0;
      
      if (stockNum === 0) {
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border border-red-200 dark:border-red-800';
      }
      if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) {
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border border-orange-200 dark:border-orange-800';
      }
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800';
    },
    
    async loadCategorias() {
      try {
        const result = await obtenerCategorias('activas');
        if (result.success) {
          this.categorias = result.data;
        }
      } catch (error) {
        console.error('Error cargando categorías:', error);
      }
    },
    
    async loadProductos() {
      this.loading = true;
      try {
        if (this.filters.categoria_id) {
          const result = await obtenerProductosPorCategoria(this.filters.categoria_id);
          if (result.success) {
            this.productosOriginales = result.data;
          }
        } else {
          const result = await obtenerProductos('activos');
          if (result.success) {
            this.productosOriginales = result.data;
          }
        }
        
        this.pagination.total = this.productosOriginales.length;
        this.pagination.lastPage = Math.ceil(this.pagination.total / this.pagination.perPage);
      } catch (error) {
        console.error('Error cargando productos:', error);
        this.productosOriginales = [];
      } finally {
        this.loading = false;
      }
    },
    
    applyFilters() {
      this.pagination.page = 1;
      this.loadProductos();
    },
    
    clearFilters() {
      this.filters = {
        estado: '',
        categoria_id: '',
        nivel_stock: '',
        busqueda: '',
        stock_min: '',
        stock_max: ''
      };
      this.pagination.page = 1;
      this.loadProductos();
    },
    
    prevPage() {
      if (this.pagination.page > 1) {
        this.pagination.page--;
      }
    },
    
    nextPage() {
      if (this.pagination.page < this.pagination.lastPage) {
        this.pagination.page++;
      }
    },
    
    async imprimirReporteCompleto() {
      if (this.productosFiltrados.length === 0) {
        this.$swal?.fire({
          icon: 'warning',
          title: 'Sin datos',
          text: 'No hay productos que coincidan con los filtros seleccionados'
        });
        return;
      }
      
      await imprimirReporteProductos(
        this.productosFiltrados,
        this.getTituloReporte(),
        this.filters.nivel_stock || 'todos'
      );
    },
    
    exportarExcel() {
      if (this.productosFiltrados.length === 0) {
        this.$swal?.fire({
          icon: 'warning',
          title: 'Sin datos',
          text: 'No hay productos que coincidan con los filtros'
        });
        return;
      }
      
      const resultado = exportarExcel(this.productosFiltrados, this.getNombreReporte());
      
      if (resultado.success) {
        this.$swal?.fire({
          icon: 'success',
          title: 'Exportación exitosa',
          text: `Se exportaron ${resultado.count} productos`
        });
      } else {
        this.$swal?.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al exportar: ' + resultado.error
        });
      }
    },
    
    async exportarPDF() {
      if (this.productosFiltrados.length === 0) {
        this.$swal?.fire({
          icon: 'warning',
          title: 'Sin datos',
          text: 'No hay productos que coincidan con los filtros'
        });
        return;
      }
      
      const resultado = await exportarPDF(this.productosFiltrados, this.getTituloReporte());
      
      if (resultado.success) {
        this.$swal?.fire({
          icon: 'success',
          title: 'PDF generado',
          text: `Se generó el PDF con ${resultado.count} productos`
        });
      } else {
        this.$swal?.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al generar PDF: ' + resultado.error
        });
      }
    },
    
    getTituloReporte() {
      const partes = [];
      
      if (this.filters.estado) {
        partes.push(this.filters.estado === 'activo' ? 'PRODUCTOS ACTIVOS' : 'PRODUCTOS INACTIVOS');
      }
      
      if (this.filters.categoria_id) {
        const categoria = this.categorias.find(c => c.idCategoria === parseInt(this.filters.categoria_id));
        if (categoria) partes.push(`CATEGORÍA: ${categoria.nombre.toUpperCase()}`);
      }
      
      const niveles = {
        sin_stock: 'SIN STOCK',
        stock_bajo: 'STOCK BAJO',
        stock_normal: 'STOCK NORMAL',
        con_stock: 'CON STOCK'
      };
      if (this.filters.nivel_stock && niveles[this.filters.nivel_stock]) {
        partes.push(niveles[this.filters.nivel_stock]);
      }
      
      if (this.filters.stock_min !== '' || this.filters.stock_max !== '') {
        let rango = 'STOCK ';
        if (this.filters.stock_min !== '' && this.filters.stock_max !== '') {
          rango += `ENTRE ${this.filters.stock_min} Y ${this.filters.stock_max}`;
        } else if (this.filters.stock_min !== '') {
          rango += `≥ ${this.filters.stock_min}`;
        } else if (this.filters.stock_max !== '') {
          rango += `≤ ${this.filters.stock_max}`;
        }
        partes.push(rango);
      }
      
      if (this.filters.busqueda) {
        partes.push(`BUSQUEDA: "${this.filters.busqueda}"`);
      }
      
      if (partes.length === 0) {
        return 'REPORTE GENERAL DE PRODUCTOS';
      }
      
      return `REPORTE DE PRODUCTOS - ${partes.join(' | ')}`;
    },
    
    getNombreReporte() {
      const partes = [];
      
      if (this.filters.estado) partes.push(this.filters.estado);
      if (this.filters.categoria_id) partes.push(`cat_${this.filters.categoria_id}`);
      if (this.filters.nivel_stock) partes.push(this.filters.nivel_stock);
      if (this.filters.busqueda) partes.push(`busqueda_${this.filters.busqueda.substring(0, 20)}`);
      
      const nombreBase = partes.length > 0 ? partes.join('_') : 'todos';
      return `reporte_productos_${nombreBase}`;
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }

.custom-scrollbar::-webkit-scrollbar { height: 4px; width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

/* Estilos para pantallas extra pequeñas */
@media (max-width: 480px) {
  .xs\:inline { display: inline; }
  .xs\:hidden { display: none; }
}
@media (min-width: 481px) {
  .xs\:inline { display: none; }
  .xs\:hidden { display: inline; }
}
</style>